import type { Dashboard } from "~/types/dashboard";
import type { Profile } from "~/types/profile";
import { createAvatar } from "@dicebear/core";
import { initials } from "@dicebear/collection";
import type {
  SendHelpRequest,
  SignupUserRequest,
  UpdateProfileRequest,
} from "~/types/server/requests";
import type { SignupOnpResponse } from "~/types/server/responses";

export function useProfile() {
  const dashboard = useState<Dashboard>("dashboard");
  const {
    public: { inpCookieDomain, phpSessionName },
  } = useRuntimeConfig();

  const jwt = useCookie("jwt", {
    domain: <string>inpCookieDomain,
  });

  const resetStates = () => {
    jwt.value = null;
    const profile: Ref<Profile | null> = useState<Profile>("profile");
    profile.value = null;
    const dashboard: Ref<Dashboard | null> = useState<Dashboard>("dashboard");
    dashboard.value = null;
    const cookie = useCookie(<string>phpSessionName, {
      domain: <string>inpCookieDomain,
    });
    cookie.value = null;
    // Effettuo il reset anche del jwt del dominio default (vedi PR#18)
    const jwtDomain = useCookie("jwt", {});
    jwtDomain.value = null;
  };

  const logout = async () => {
    resetStates();
    return await $fetch("/api/user/logout");
  };

  const login = async (data: {
    username: string;
    password: string;
    captcha: string;
  }) => {
    resetStates();
    try {
      const r: any = await $fetch(`/api/user/login`, {
        method: "POST",
        credentials: "include",
        body: data,
      });
      if (r && r.object) {
        jwt.value = r.object.jwt_token;
        // Setto il cookie per retro-compatibilità con Modx
        const cookie = useCookie(<string>phpSessionName, {
          domain: <string>inpCookieDomain,
        });
        cookie.value = r.object.sessionid;
      }
      return r;
    } catch (e: any) {
      return {
        success: false,
        message: e.message,
        status: e.status,
      };
    }
  };

  const loadProfile = async () => {
    try {
      const user = useState<Profile>("profile");
      const r: any = await $fetch(`/api/user/isloggedin`, {
        method: "POST",
        responseType: "json",
        credentials: "include",
        body: {
          jwt: jwt.value,
        },
      });
      if (r.success) {
        user.value = r.object;
        return r.object;
      } else return false;
    } catch (e: any) {
      console.log(e);
      return false;
    }
  };

  const sendActivationEmail = async () => {
    try {
      const user = useState<Profile>("profile");
      const r: any = await $fetch(`/api/user/sendactivationemail`, {
        method: "POST",
        responseType: "json",
        credentials: "include",
        body: {
          id: user.value.id,
          email: user.value.email,
        },
      });
      if (r.success) {
        user.value = r.object;
        return r.object;
      } else return false;
    } catch (e: any) {
      console.log(e);
      return false;
    }
  };

  const signup = async (data: SignupUserRequest) => {
    try {
      const r: any = await $fetch(`/api/user/signup`, {
        method: "POST",
        credentials: "include",
        body: data,
      });
      if (r && r.success) {
        jwt.value = r.object.jwt_token;
      }
      return r;
    } catch (e: any) {
      return e;
    }
  };

  const forgotPassword = async (data: { email: string }) => {
    try {
      const r: any = await $fetch(`/api/user/forgotpassword`, {
        method: "POST",
        credentials: "include",
        body: data,
      });
      return r;
    } catch (e: any) {
      return e;
    }
  };

  const signupOnp = async (data: {
    nome_onp: string;
    tax_code: string;
  }): Promise<SignupOnpResponse> => {
    try {
      const r: any = await $fetch(`/api/user/signuponp`, {
        method: "POST",
        credentials: "include",
        body: {
          jwt: jwt.value,
          ...data,
        },
      });
      return r;
    } catch (e: any) {
      return {
        success: false,
        message: e.message || "Errore durante la registrazione dell'ente",
      };
    }
  };

  const changePassword = async (data: { password: string }) => {
    try {
      const r: any = await $fetch(`/api/user/changepassword`, {
        method: "POST",
        credentials: "include",
        body: {
          jwt: jwt.value,
          ...data,
        },
      });
      return r;
    } catch (e: any) {
      return e;
    }
  };

  const updateProfile = async (data: UpdateProfileRequest) => {
    try {
      const r: any = await $fetch(`/api/user/updateprofile`, {
        method: "POST",
        credentials: "include",
        body: {
          jwt: jwt.value,
          ...data,
        },
      });
      return r;
    } catch (e: any) {
      return e;
    }
  };

  const sendHelpRequest = async (data: SendHelpRequest) => {
    try {
      const r: any = await $fetch(`/api/user/helprequest`, {
        method: "POST",
        credentials: "include",
        body: {
          jwt: jwt.value,
          ...data,
        },
      });
      return r;
    } catch (e: any) {
      return e;
    }
  };

  const user = useState<Profile>("profile");

  const isAdmin = computed(() => {
    return user.value?.isA ?? false;
  });

  const userName = computed(() => {
    return user.value?.nome ?? "";
  });

  const userId = computed(() => {
    return user.value.id;
  });

  const userMail = computed(() => {
    return user.value.email;
  });

  const userLastName = computed(() => {
    return user.value.cognome;
  });

  const userAvatar = computed(() => {
    const user = useState<Profile>("profile");
    const userAvatar = createAvatar(initials, {
      seed: user.value?.nome ?? user.value?.email,
      size: 40,
    });

    return userAvatar.toDataUri();
  });

  const organizationAvatar = computed(() => {
    if (!hasOrganization.value) return;

    if (organization.value && organization.value.logo?.url) {
      return organization.value.logo?.url;
    } else {
      const dashboard = useState<Dashboard>("dashboard");

      const avatar = createAvatar(initials, {
        seed: dashboard.value.config.ente?.name,
        size: 40,
      });
      return avatar.toDataUri();
    }
  });

  const organization = computed(() => {
    if (!hasOrganization.value) return false;
    const dashboard = useState<Dashboard>("dashboard");
    return dashboard.value.config.ente;
  });

  const organizationName = computed(() => {
    if (!hasOrganization.value || !dashboard.value.config.ente?.name) {
      if (dashboard.value.user.company) {
        return dashboard.value.user.company;
      }
      return "La tua organizzazione";
    }
    return <string>dashboard.value.config.ente?.name;
  });

  const organizationDescription = computed(() => {
    if (!hasOrganization.value) return "";
    return dashboard.value.config.ente?.description;
  });

  const organizationLegalStatus = computed(() => {
    if (!hasOrganization.value) return "";
    return dashboard.value.config.ente?.legal_status;
  });

  const organizationOnpType = computed(() => {
    if (!hasOrganization.value) return "";
    return dashboard.value.config.ente?.onp_type;
  });

  const organizationPageUrl = computed(() => {
    if (!hasOrganization.value) return false;
    return dashboard.value.config.ente?.resource?.url;
  });

  /**
   * Ritorna le informazioni sull'ente associato all'utente
   * @return {boolean}
   */
  const hasOrganization = computed(() => {
    if (!dashboard.value) return false;
    if (!dashboard.value.config) return false;
    if (!dashboard.value.config.ente) return false;
    return true;
  });

  /**
   * Ritorna le informazioni sull'ente associato all'utente
   */
  const registry = computed(() => {
    if (!dashboard.value) return false;
    if (!dashboard.value.config) return false;
    if (!dashboard.value.config.registry) return false;
    return dashboard.value.config.registry;
  });

  /**
   * Ritorna l'anno di costituzione dell'ente
   * @return {number}
   */
  const establishmentYear = computed(() => {
    if (!hasOrganization.value) {
      return false;
    }
    return organization.value && organization.value.establishment_year
      ? parseInt(organization.value.establishment_year)
      : false;
  });

  return {
    changePassword,
    organizationDescription,
    organizationLegalStatus,
    organizationOnpType,
    updateProfile,
    sendHelpRequest,
    registry,
    establishmentYear,
    isAdmin,
    hasOrganization,
    organizationAvatar,
    organizationName,
    organizationPageUrl,
    user,
    userName,
    userMail,
    userLastName,
    userId,
    userAvatar,
    signupOnp,
    loadProfile,
    forgotPassword,
    sendActivationEmail,
    login,
    signup,
    logout,
  };
}
